import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import {
  useAutomationConfig,
  useUpdateAutomationConfig,
  type AutomationConfig,
} from "@/hooks/use-automation-config";
import { useAccounts } from "@/hooks/use-accounts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { AdAccountSelector } from "@/components/ads/ad-account-selector";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

// Writable fields only — paused-state fields (autopilotPausedAt/PauseReason)
// are read from `data` directly so the dirty check stays stable.
type Draft = Pick<
  AutomationConfig,
  | "aiReviewEnabled"
  | "aiReviewAutoApprove"
  | "aiReviewThreshold"
  | "autoScheduleEnabled"
  | "autoScheduleDaysAhead"
  | "inboxAiDraftsEnabled"
  | "inboxAutoReplyEnabled"
  | "inboxAutoReplyThreshold"
  | "inboxAutoReplyMaxPerConv"
  | "autopilotEnabled"
  | "autopilotCadenceHours"
  | "autopilotLookaheadDays"
  | "autopilotMaxPiecesPerRun"
  | "autopilotMediaPolicy"
  | "autopilotPlatformTargets"
  | "adsEnabled"
  | "adsMode"
  | "adsMinEngagementRate"
  | "adsMinImpressions"
  | "adsEvalWindowHours"
  | "adsBoostDurationDays"
  | "adsGoal"
> & {
  // Decimal columns arrive as strings on GET but the PUT validator requires
  // numbers — the draft always holds numbers (pickConfig normalizes).
  adsMonthlyBudgetUsd: number | null;
  adsPerBoostBudgetUsd: number;
};

function pickConfig(data: AutomationConfig): Draft {
  return {
    aiReviewEnabled: data.aiReviewEnabled,
    aiReviewAutoApprove: data.aiReviewAutoApprove,
    aiReviewThreshold: data.aiReviewThreshold,
    autoScheduleEnabled: data.autoScheduleEnabled,
    autoScheduleDaysAhead: data.autoScheduleDaysAhead,
    inboxAiDraftsEnabled: data.inboxAiDraftsEnabled ?? false,
    inboxAutoReplyEnabled: data.inboxAutoReplyEnabled ?? false,
    inboxAutoReplyThreshold: data.inboxAutoReplyThreshold ?? 85,
    inboxAutoReplyMaxPerConv: data.inboxAutoReplyMaxPerConv ?? 5,
    autopilotEnabled: data.autopilotEnabled ?? false,
    autopilotCadenceHours: data.autopilotCadenceHours ?? 24,
    autopilotLookaheadDays: data.autopilotLookaheadDays ?? 7,
    autopilotMaxPiecesPerRun: data.autopilotMaxPiecesPerRun ?? 3,
    autopilotMediaPolicy: data.autopilotMediaPolicy ?? "copy_image",
    autopilotPlatformTargets: data.autopilotPlatformTargets ?? {},
    adsEnabled: data.adsEnabled ?? false,
    adsMode: data.adsMode ?? "suggest",
    // Decimal → string on GET; PUT requires number (or null = fail closed).
    adsMonthlyBudgetUsd:
      data.adsMonthlyBudgetUsd === null || data.adsMonthlyBudgetUsd === undefined
        ? null
        : Number(data.adsMonthlyBudgetUsd),
    adsPerBoostBudgetUsd: Number(data.adsPerBoostBudgetUsd ?? 25),
    adsMinEngagementRate: data.adsMinEngagementRate ?? 0.05,
    adsMinImpressions: data.adsMinImpressions ?? 1000,
    adsEvalWindowHours: data.adsEvalWindowHours ?? 48,
    adsBoostDurationDays: data.adsBoostDurationDays ?? 7,
    adsGoal: data.adsGoal ?? "engagement",
  };
}

export function BrandSettingsAutomationPage() {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";

  const { data, isLoading } = useAutomationConfig(brandId);
  const update = useUpdateAutomationConfig(brandId);
  const { data: accounts } = useAccounts(brandId);
  const [draft, setDraft] = useState<Draft | null>(null);
  useEffect(() => {
    if (data) setDraft(pickConfig(data));
  }, [data]);

  if (isLoading || !draft || !data) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(pickConfig(data));
  const reviewControlsDisabled = !draft.aiReviewEnabled || !canManageBrand;
  const autopilotControlsDisabled = !draft.autopilotEnabled || !canManageBrand;
  const adsControlsDisabled = !draft.adsEnabled || !canManageBrand;

  const activePlatforms = Array.from(
    new Set(
      (accounts ?? [])
        .filter((a) => a.status === "ACTIVE")
        .map((a) => String(a.platform)),
    ),
  );

  function setPlatformTarget(platform: string, raw: string) {
    if (!draft) return;
    const targets = { ...draft.autopilotPlatformTargets };
    if (raw === "") {
      // Missing key = server default (3/wk).
      delete targets[platform];
    } else {
      targets[platform] = clamp(Number(raw), 1, 21);
    }
    setDraft({ ...draft, autopilotPlatformTargets: targets });
  }

  async function save() {
    if (!draft) return;
    await update.mutateAsync(draft);
    toast.success(t("automation.saved"));
  }

  // Dedicated single-field mutation — never bundled with the draft save.
  async function resumeAutopilot() {
    await update.mutateAsync({ resumeAutopilot: true });
    toast.success(t("automation.autopilotResumed"));
  }

  // Standalone action mutations — never merged into the draft save.
  async function resumeAds() {
    if (
      !window.confirm(t("automation.ads.resumeConfirm"))
    ) {
      return;
    }
    await update.mutateAsync({ resumeAds: true });
    toast.success(t("automation.ads.resumed"));
  }

  async function pauseAllAds() {
    if (
      !window.confirm(t("automation.ads.pauseConfirm"))
    ) {
      return;
    }
    await update.mutateAsync({ pauseAds: true });
    toast.success(t("automation.ads.paused"));
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("automation.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("automation.subtitle")}
        </p>
      </header>

      <section className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("automation.aiReview.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("automation.aiReview.subtitle")}
            </p>
          </div>
          <Switch
            checked={draft.aiReviewEnabled}
            disabled={!canManageBrand}
            onCheckedChange={(checked) => setDraft({ ...draft, aiReviewEnabled: checked })}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.aiReview.threshold")}</label>
          <div className="flex items-center gap-3 max-w-md">
            <input
              type="range"
              min={0}
              max={100}
              value={draft.aiReviewThreshold}
              disabled={reviewControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, aiReviewThreshold: clamp(Number(e.target.value), 0, 100) })
              }
              className="flex-1"
            />
            <input
              type="number"
              min={0}
              max={100}
              value={draft.aiReviewThreshold}
              disabled={reviewControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, aiReviewThreshold: clamp(Number(e.target.value), 0, 100) })
              }
              className="w-20 rounded border px-2 py-1 text-sm"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("automation.aiReview.thresholdHint")}
          </p>
        </div>

        <div className="ml-4 border-l pl-4 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium">{t("automation.aiReview.autoApprove")}</span>
              <p className="text-xs text-muted-foreground">
                {t("automation.aiReview.autoApproveHint")}
              </p>
            </div>
            <Switch
              checked={draft.aiReviewAutoApprove}
              disabled={reviewControlsDisabled}
              onCheckedChange={(checked) => setDraft({ ...draft, aiReviewAutoApprove: checked })}
            />
          </div>
          {draft.aiReviewAutoApprove && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 rounded-md p-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <p>{t("automation.aiReview.autoApproveWarning")}</p>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("automation.autoSchedule.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("automation.autoSchedule.subtitle")}
            </p>
          </div>
          <Switch
            checked={draft.autoScheduleEnabled}
            disabled={!canManageBrand}
            onCheckedChange={(checked) => setDraft({ ...draft, autoScheduleEnabled: checked })}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.autoSchedule.daysAhead")}</label>
          <div>
            <input
              type="number"
              min={1}
              max={30}
              value={draft.autoScheduleDaysAhead}
              disabled={!draft.autoScheduleEnabled || !canManageBrand}
              onChange={(e) =>
                setDraft({ ...draft, autoScheduleDaysAhead: clamp(Number(e.target.value), 1, 30) })
              }
              className="w-20 rounded border px-2 py-1 text-sm"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("automation.autoSchedule.daysAheadHintBefore")}
            <Link to={`/${brand.slug}/settings/scheduling`} className="underline hover:text-foreground">
              {t("automation.autoSchedule.schedulingLink")}
            </Link>
            {t("automation.autoSchedule.daysAheadHintAfter")}
          </p>
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-4">
        {data.autopilotPausedAt && (
          <div className="flex items-start justify-between gap-3 rounded-md bg-amber-500/10 p-3 text-sm text-amber-400">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <p>
                {t(`automation.autopilot.paused.${data.autopilotPauseReason ?? ""}`, { defaultValue: t("automation.autopilot.paused.default") })}{" "}
                <span className="text-amber-400/70">
                  ({formatDate(new Date(data.autopilotPausedAt), locale, { dateStyle: "medium", timeStyle: "short" })})
                </span>
              </p>
            </div>
            {canManageBrand && (
              <Button
                size="sm"
                variant="outline"
                disabled={update.isPending}
                onClick={() => void resumeAutopilot()}
              >
                {t("automation.autopilot.resume")}
              </Button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("automation.autopilot.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("automation.autopilot.subtitle")}
            </p>
          </div>
          <Switch
            checked={draft.autopilotEnabled}
            disabled={!canManageBrand}
            onCheckedChange={(checked) => setDraft({ ...draft, autopilotEnabled: checked })}
          />
        </div>

        {draft.autopilotEnabled && (
          <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 rounded-md p-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>{t("automation.autopilot.warning")}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 max-w-2xl">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.autopilot.cadence")}</label>
            <input
              type="number"
              min={6}
              max={168}
              value={draft.autopilotCadenceHours}
              disabled={autopilotControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, autopilotCadenceHours: clamp(Number(e.target.value), 6, 168) })
              }
              className="w-20 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">{t("automation.autopilot.cadenceHint")}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.autopilot.lookahead")}</label>
            <input
              type="number"
              min={1}
              max={30}
              value={draft.autopilotLookaheadDays}
              disabled={autopilotControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, autopilotLookaheadDays: clamp(Number(e.target.value), 1, 30) })
              }
              className="w-20 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">{t("automation.autopilot.lookaheadHint")}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.autopilot.maxPieces")}</label>
            <input
              type="number"
              min={1}
              max={10}
              value={draft.autopilotMaxPiecesPerRun}
              disabled={autopilotControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, autopilotMaxPiecesPerRun: clamp(Number(e.target.value), 1, 10) })
              }
              className="w-20 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">{t("automation.autopilot.maxPiecesHint")}</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.autopilot.mediaPolicy")}</label>
          <div>
            <select
              value={draft.autopilotMediaPolicy}
              disabled={autopilotControlsDisabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  autopilotMediaPolicy: e.target.value as "copy_only" | "copy_image",
                })
              }
              className="rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="copy_only">{t("automation.autopilot.mediaPolicyCopyOnly")}</option>
              <option value="copy_image">{t("automation.autopilot.mediaPolicyCopyImage")}</option>
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("automation.autopilot.mediaPolicyVideoNote")}
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.autopilot.platformTargets")}</label>
          {activePlatforms.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("automation.autopilot.noAccountsBefore")}
              <Link
                to={`/${brand.slug}/settings/accounts`}
                className="underline hover:text-foreground"
              >
                {t("automation.autopilot.accountsLink")}
              </Link>
              {t("automation.autopilot.noAccountsAfter")}
            </p>
          ) : (
            <div className="space-y-2 max-w-md">
              {activePlatforms.map((platform) => (
                <div key={platform} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <PlatformIcon platform={platform} size={16} />
                    <span className="text-sm capitalize">{platform.toLowerCase()}</span>
                  </div>
                  <input
                    type="number"
                    min={1}
                    max={21}
                    placeholder={t("automation.autopilot.platformDefault")}
                    value={draft.autopilotPlatformTargets[platform] ?? ""}
                    disabled={autopilotControlsDisabled}
                    onChange={(e) => setPlatformTarget(platform, e.target.value)}
                    className="w-28 rounded border px-2 py-1 text-sm"
                  />
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                {t("automation.autopilot.platformDefaultNote")}
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-4">
        {data.adsPausedAt ? (
          <div className="flex items-start justify-between gap-3 rounded-md bg-amber-500/10 p-3 text-sm text-amber-400">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <p>
                {t(`automation.ads.pauseReasons.${data.adsPauseReason ?? ""}`, { defaultValue: t("automation.ads.pauseReasons.default") })}{" "}
                <span className="text-amber-400/70">
                  ({formatDate(new Date(data.adsPausedAt), locale, { dateStyle: "medium", timeStyle: "short" })})
                </span>
              </p>
            </div>
            {canManageBrand && (
              <Button
                size="sm"
                variant="outline"
                disabled={update.isPending}
                onClick={() => void resumeAds()}
              >
                {t("automation.ads.resumeAds")}
              </Button>
            )}
          </div>
        ) : (
          data.adsEnabled &&
          canManageBrand && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                disabled={update.isPending}
                onClick={() => void pauseAllAds()}
              >
                {t("automation.ads.pauseAll")}
              </Button>
            </div>
          )
        )}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("automation.ads.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.subtitle")}
            </p>
          </div>
          <Switch
            checked={draft.adsEnabled}
            disabled={!canManageBrand}
            onCheckedChange={(checked) => setDraft({ ...draft, adsEnabled: checked })}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.ads.mode")}</label>
          <div>
            <select
              value={draft.adsMode}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, adsMode: e.target.value as "suggest" | "auto" })
              }
              className="rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="suggest">{t("automation.ads.modeSuggest")}</option>
              <option value="auto">{t("automation.ads.modeAuto")}</option>
            </select>
          </div>
          {draft.adsMode === "auto" && draft.adsEnabled && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 rounded-md p-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <p>{t("automation.ads.autoWarning")}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-2xl">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.monthlyCap")}</label>
            <input
              type="number"
              min={1}
              max={100000}
              value={draft.adsMonthlyBudgetUsd ?? ""}
              placeholder={t("automation.ads.noCap")}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  adsMonthlyBudgetUsd:
                    e.target.value === "" ? null : clamp(Number(e.target.value), 1, 100000),
                })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.noCapNote")}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.perBoostBudget")}</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={draft.adsPerBoostBudgetUsd}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, adsPerBoostBudgetUsd: clamp(Number(e.target.value), 1, 1000) })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.perBoostNote")}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.minEngagement")}</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={Math.round(draft.adsMinEngagementRate * 10000) / 100}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  adsMinEngagementRate: clamp(Number(e.target.value), 0, 100) / 100,
                })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.minEngagementNote")}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.minImpressions")}</label>
            <input
              type="number"
              min={0}
              max={10000000}
              value={draft.adsMinImpressions}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  adsMinImpressions: clamp(Number(e.target.value), 0, 10000000),
                })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.minImpressionsNote")}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.evalWindow")}</label>
            <input
              type="number"
              min={24}
              max={168}
              value={draft.adsEvalWindowHours}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, adsEvalWindowHours: clamp(Number(e.target.value), 24, 168) })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.evalWindowNote")}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.ads.boostDuration")}</label>
            <input
              type="number"
              min={1}
              max={30}
              value={draft.adsBoostDurationDays}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({ ...draft, adsBoostDurationDays: clamp(Number(e.target.value), 1, 30) })
              }
              className="w-28 rounded border px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {t("automation.ads.boostDurationNote")}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t("automation.ads.goal")}</label>
          <div>
            <select
              value={draft.adsGoal}
              disabled={adsControlsDisabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  adsGoal: e.target.value as Draft["adsGoal"],
                })
              }
              className="rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="engagement">{t("automation.ads.goalEngagement")}</option>
              <option value="traffic">{t("automation.ads.goalTraffic")}</option>
              <option value="awareness">{t("automation.ads.goalAwareness")}</option>
              <option value="video_views">{t("automation.ads.goalVideoViews")}</option>
            </select>
          </div>
        </div>

        {canManageBrand && <AdAccountSelector brandId={brandId} brandSlug={brand.slug} />}
      </section>

      <section className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("automation.inbox.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("automation.inbox.subtitle")}
            </p>
          </div>
          <Switch
            checked={draft.inboxAiDraftsEnabled}
            disabled={!canManageBrand}
            onCheckedChange={(checked) =>
              setDraft({
                ...draft,
                inboxAiDraftsEnabled: checked,
                // Auto-reply cannot run without drafts.
                inboxAutoReplyEnabled: checked ? draft.inboxAutoReplyEnabled : false,
              })
            }
          />
        </div>

        <div className="ml-4 border-l pl-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium">{t("automation.inbox.autoReply")}</span>
              <p className="text-xs text-muted-foreground">
                {t("automation.inbox.autoReplyHint")}
              </p>
            </div>
            <Switch
              checked={draft.inboxAutoReplyEnabled}
              disabled={!draft.inboxAiDraftsEnabled || !canManageBrand}
              onCheckedChange={(checked) =>
                setDraft({ ...draft, inboxAutoReplyEnabled: checked })
              }
            />
          </div>
          {draft.inboxAutoReplyEnabled && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 rounded-md p-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <p>{t("automation.inbox.autoReplyWarning")}</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.inbox.confidenceThreshold")}</label>
            <div>
              <input
                type="number"
                min={50}
                max={100}
                value={draft.inboxAutoReplyThreshold}
                disabled={!draft.inboxAutoReplyEnabled || !draft.inboxAiDraftsEnabled || !canManageBrand}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    inboxAutoReplyThreshold: clamp(Number(e.target.value), 50, 100),
                  })
                }
                className="w-20 rounded border px-2 py-1 text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("automation.inbox.confidenceHint")}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("automation.inbox.maxAutoReplies")}</label>
            <div>
              <input
                type="number"
                min={1}
                max={20}
                value={draft.inboxAutoReplyMaxPerConv}
                disabled={!draft.inboxAutoReplyEnabled || !draft.inboxAiDraftsEnabled || !canManageBrand}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    inboxAutoReplyMaxPerConv: clamp(Number(e.target.value), 1, 20),
                  })
                }
                className="w-20 rounded border px-2 py-1 text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("automation.inbox.maxAutoRepliesHint")}
            </p>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || update.isPending || !canManageBrand}>
          {update.isPending ? t("actions.saving", { ns: "common" }) : t("actions.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
